import {
  AdminRadioInput,
  Button,
  Div,
  DropdownMenuComponent,
  H5,
  HeaderIconWrapper,
  Icon,
  Overlay,
  Separator,
  SlidersIcon,
  TextButton,
  UserRole,
  lightTheme,
  overlayAnimationVariant,
  useDisableBodyScroll,
} from '@vgrubs/components';
import 'flatpickr/dist/flatpickr.css';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useId, useState } from 'react';

export const AdminRolePicker = ({
  onChange,
  value,
}: {
  onChange: (value: UserRole[]) => void;
  value: UserRole[];
}) => {
  const [dropdown, setDropdown] = useState(false);
  const [roles, setRoles] = useState(value);
  const menuId = useId();

  useDisableBodyScroll(dropdown);

  useEffect(() => {
    setRoles(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdown]);

  // a11y: ESC closes the picker (matches AdminOptionsDropdown).
  useEffect(() => {
    if (!dropdown) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setDropdown(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dropdown]);

  const roleFilters = [
    { name: 'Admin', value: 'admin' },
    { name: 'Support', value: 'support' },
    { name: 'Partner', value: 'partner' },
    { name: 'Manager', value: 'manager' },
    { name: 'Sales Agent', value: 'salesAgent' },
    { name: 'Sales Manager', value: 'salesManager' },
    { name: 'Staff', value: 'staff' },
    { name: 'Marketing', value: 'marketing' },
  ];

  const handleClearFilters = () => {
    setRoles([]);
    onChange([]);
    setDropdown(false);
  };

  const hasActiveFilters = value.length > 0;

  return (
    <AnimatePresence>
      <Div $relative>
        <HeaderIconWrapper
          $opened={dropdown}
          onClick={() => setDropdown((prev) => !prev)}
          $marker={value.length > 0}
          aria-haspopup="menu"
          aria-expanded={dropdown}
          aria-controls={menuId}
          aria-label={
            value.length > 0
              ? `Filter by role (${value.length} active)`
              : 'Filter by role'
          }
        >
          <Icon $size="24" color={lightTheme.primary}>
            <SlidersIcon />
          </Icon>
        </HeaderIconWrapper>
        <DropdownMenuComponent
          isOpen={dropdown}
          toLeft
          padding="4px 16px 16px 16px"
        >
          <Div
            id={menuId}
            role="group"
            aria-label="Filter by role"
            $width="250px"
            $widthSM="220px"
          >
            {roleFilters.map(({ name, value }, i) => (
              <div key={i}>
                <AdminRadioInput
                  label={name}
                  checked={roles.includes(value as UserRole)}
                  onClick={() =>
                    setRoles(
                      roles.includes(value as UserRole)
                        ? roles.filter((role) => role !== (value as UserRole))
                        : [...roles, value as UserRole]
                    )
                  }
                />

                {i !== roleFilters.length - 1 && <Separator />}
              </div>
            ))}
          </Div>
          {hasActiveFilters && (
            <Button
              $small
              $selected
              $gray
              onClick={handleClearFilters}
              $w="100%"
              $wSM="100%"
              $mb="8"
              $mt="4"
            >
              Clear Filters
            </Button>
          )}
          <Button
            $selected
            $w="100%"
            $mt="4"
            $wSM="100%"
            onClick={() => {
              onChange(roles);
              setDropdown(false);
            }}
            $small
          >
            Confirm
          </Button>
        </DropdownMenuComponent>
      </Div>

      {dropdown && (
        <Overlay
          key="modalOverlay"
          aria-hidden="true"
          variants={overlayAnimationVariant}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={() => setDropdown(false)}
        />
      )}
    </AnimatePresence>
  );
};
